import React from 'react';
import { Conversation } from '../types';

interface ConversationListItemProps {
  conversation: Conversation;
  isSelected?: boolean;
  onClick?: () => void;
  userRole: 'candidate' | 'recruiter';
}

/**
 * Conversation list item component
 * Improved: Larger avatars, better spacing, subtle selection indicator
 */
const ConversationListItem: React.FC<ConversationListItemProps> = ({
  conversation,
  isSelected = false,
  onClick,
  userRole
}) => {
  // Get unread count based on user role
  const unreadCount = userRole === 'candidate'
    ? conversation.unread_count.candidate
    : conversation.unread_count.recruiter;

  // Determine display name (company for candidate, candidate name for recruiter)
  const displayName = userRole === 'candidate'
    ? conversation.company_name
    : conversation.candidate_name;

  // Get first letter for avatar fallback
  const avatarLetter = displayName?.charAt(0).toUpperCase() || '?';

  // Format timestamp
  const formatTimestamp = (timestamp: string): string => {
    const now = new Date();
    const messageTime = new Date(timestamp);
    const diffInMinutes = Math.floor((now.getTime() - messageTime.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) {
      return 'Just now';
    } else if (diffInMinutes < 60) {
      return `${diffInMinutes}m ago`;
    } else if (diffInMinutes < 1440) {
      const hours = Math.floor(diffInMinutes / 60);
      return `${hours}h ago`;
    } else if (diffInMinutes < 10080) {
      const days = Math.floor(diffInMinutes / 1440);
      return `${days}d ago`;
    } else {
      return messageTime.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div
      onClick={onClick}
      className={`
        relative flex items-center px-4 py-3 gap-3 w-full
        cursor-pointer transition-all duration-200 ease-out
        border-b border-[#F1F3F4]
        ${isSelected
          ? 'bg-[#EBF4FB]'
          : 'hover:bg-[#FAFBFC]'}
      `}
    >
      {/* Selected indicator - subtle left accent */}
      {isSelected && (
        <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-[#015FA6] rounded-r-full" />
      )}

      {/* Avatar */}
      <div className="relative flex-shrink-0">
        {conversation.is_david ? (
          /* David bot avatar — purple gradient with bot icon */
          <div className="w-12 h-12 rounded-xl overflow-hidden bg-gradient-to-br from-[#7C3AED] to-[#5B21B6] shadow-sm flex items-center justify-center">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="3" y="4" width="18" height="14" rx="3" stroke="white" strokeWidth="1.5"/>
              <circle cx="9" cy="11" r="1.5" fill="white"/>
              <circle cx="15" cy="11" r="1.5" fill="white"/>
              <path d="M8 20H16" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
              <path d="M12 18V20" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
            </svg>
          </div>
        ) : (
          <div className="w-12 h-12 rounded-xl overflow-hidden bg-[#015FA6] shadow-sm">
            {conversation.company_logo ? (
              <img
                src={conversation.company_logo}
                alt={displayName}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white font-manrope font-bold text-base">
                {avatarLetter}
              </div>
            )}
          </div>
        )}

        {/* Online indicator with glow */}
        {conversation.is_online && !conversation.is_david && (
          <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-[#22C55E] border-2 border-white rounded-full shadow-[0_0_0_2px_rgba(34,197,94,0.2)]" />
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Top row: Name and timestamp */}
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <span className={`font-manrope text-sm truncate flex items-center gap-1.5 ${unreadCount > 0 ? 'font-bold text-[#0B111E]' : 'font-semibold text-[#1A1A1A]'}`}>
            {displayName || 'Unknown'}
            {conversation.is_david && (
              <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-bold bg-[#7C3AED]/10 text-[#7C3AED] uppercase tracking-wider">Bot</span>
            )}
          </span>
          <span className="flex-shrink-0 font-manrope text-[10px] font-medium text-[#9CA3AF] uppercase tracking-wide">
            {conversation.last_message_at ? formatTimestamp(conversation.last_message_at) : ''}
          </span>
        </div>

        {/* Role / job title row */}
        {(conversation.job_title || conversation.company_name) && (
          <p className="font-manrope text-xs font-medium text-[#015FA6] truncate mb-1">
            {userRole === 'candidate'
              ? conversation.job_title
                ? `${conversation.job_title}${conversation.company_name ? ` @ ${conversation.company_name}` : ''}`
                : conversation.company_name
              : conversation.job_title || ''}
          </p>
        )}

        {/* Bottom row: Last message and unread dot */}
        <div className="flex items-center gap-2">
          <p className={`flex-1 font-manrope text-sm truncate ${unreadCount > 0 ? 'text-[#374151] font-semibold' : 'text-[#6B7280]'}`}>
            {conversation.last_message?.content || 'Start a conversation...'}
          </p>

          {/* Unread dot */}
          {unreadCount > 0 && (
            <div className="flex-shrink-0 w-2 h-2 bg-[#015FA6] rounded-full mt-0.5" />
          )}
        </div>
      </div>
    </div>
  );
};

export default ConversationListItem;
